/**
 * Typed REST client for the ETT backend.
 *
 * Auth: a token provider is registered once at boot from `AuthProvider`.
 * Every request reads the latest access token through that callback so we
 * always send a fresh value (after silent refresh).
 *
 * Errors: the client throws `APIError` on non-2xx — TanStack Query catches
 * them and propagates to component error states.
 */

import type {
  APIError as APIErrorShape,
  AuditPage,
  CommitRequest,
  CommitResponse,
  ErrorListItem,
  ErrorListResponse,
  ErrorType,
  FlagRequest,
  FlagResponse,
  HealthSummary,
  LLMProvider,
  MapEntitiesResponse,
  PipelineStatus,
  QueryRequest,
  QueryResponse,
  ResolutionAction,
  RssPollResult,
  Source,
  SourceType,
  SubgraphResponse,
  ThreadDetail,
  ThreadSummary,
} from '@/types/api';

// Empty VITE_API_BASE_URL means "same-origin" (production behind a reverse
// proxy that fronts both /api and /). Fall back to localhost:8000 only when
// running outside the browser (SSR / tests).
const BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000');

// ─── Token provider ─────────────────────────────────────────────

type TokenProvider = () => string | null;

let tokenProvider: TokenProvider = () => null;

export function setTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

// ─── APIError class ─────────────────────────────────────────────

export class APIError extends Error {
  status: number;
  detail: string | Record<string, unknown>;

  constructor(payload: APIErrorShape) {
    const detailMsg =
      typeof payload.detail === 'string'
        ? payload.detail
        : JSON.stringify(payload.detail);
    super(`HTTP ${payload.status}: ${detailMsg}`);
    this.name = 'APIError';
    this.status = payload.status;
    this.detail = payload.detail;
  }
}

// ─── Core request ───────────────────────────────────────────────

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null | string[]>;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = new URL(path.startsWith('http') ? path : `${BASE_URL}${path}`);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v === undefined || v === null) continue;
      if (Array.isArray(v)) {
        for (const item of v) url.searchParams.append(k, String(item));
      } else {
        url.searchParams.set(k, String(v));
      }
    }
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  const token = tokenProvider();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url.toString(), {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    credentials: 'omit',
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!response.ok) {
    const detail =
      parsed && typeof parsed === 'object' && 'detail' in parsed
        ? (parsed as { detail: unknown }).detail
        : parsed || response.statusText;
    throw new APIError({
      status: response.status,
      detail: detail as APIErrorShape['detail'],
    });
  }

  return parsed as T;
}

// ─── Resource modules ───────────────────────────────────────────

export const queryApi = {
  submit: (req: QueryRequest, signal?: AbortSignal) =>
    request<QueryResponse>('/api/v1/query', { method: 'POST', body: req, signal }),

  listThreads: () => request<ThreadSummary[]>('/api/v1/threads'),

  getThread: (threadId: string) =>
    request<ThreadDetail>(`/api/v1/threads/${threadId}`),

  flag: (turnId: string, payload: FlagRequest) =>
    request<FlagResponse>(`/api/v1/query/${turnId}/flag`, {
      method: 'POST',
      body: payload,
    }),
};

export type AgentRunStatus = 'done' | 'halted' | 'not_run';

export interface AgentStatus {
  key: string;
  label: string;
  status: AgentRunStatus;
  halt_reason: string | null;
  notes: string | null;
}

export interface PipelineRun {
  source_id: string;
  run_id: string;
  run_timestamp: string;
  source_url: string;
  source_type: string | null;
  final_status: string | null;
  halt_reason: string | null;
  error_types: string[];
  agents: AgentStatus[];
}

export interface InFlightDocument {
  source_id: string;
  source_url: string;
  source_type: string;
  document_status: string;
  ingestion_ts: string;
  waiting_seconds: number;
}

export interface InFlightResponse {
  total: number;
  items: InFlightDocument[];
}

export interface PipelineRunsResponse {
  runs: PipelineRun[];
  total: number;
  limit: number;
  offset: number;
}

export const pipelineApi = {
  status: () => request<PipelineStatus>('/api/v1/pipeline/status'),
  batchSummary: (batchId: string) =>
    request<unknown>(`/api/v1/pipeline/batches/${batchId}/summary`),
  trigger: () =>
    request<{ status: string }>('/api/v1/pipeline/trigger', { method: 'POST' }),

  inFlight: (limit = 50) =>
    request<InFlightResponse>('/api/v1/pipeline/in-flight', {
      query: { limit },
    }),
  commit: (payload: CommitRequest) =>
    request<CommitResponse>('/api/v1/pipeline/commit', {
      method: 'POST',
      body: payload,
    }),
  runDrc: (sourceId: string, runId: string) =>
    request<Record<string, unknown>>(`/api/v1/pipeline/runs/${sourceId}/${runId}`),

  retry: (sourceId: string) =>
    request<{ source_id: string; run_id: string; mode: string }>(
      `/api/v1/pipeline/runs/${sourceId}/retry`,
      { method: 'POST' },
    ),

  runs: (params: {
    final_status?: string | undefined;
    source_type?: string | undefined;
    limit?: number | undefined;
    offset?: number | undefined;
  } = {}) =>
    request<PipelineRunsResponse>('/api/v1/pipeline/runs', {
      query: {
        final_status: params.final_status,
        source_type: params.source_type,
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      },
    }),

  commits: (limit = 50) =>
    request<CommitHistoryRow[]>('/api/v1/pipeline/commits', {
      query: { limit },
    }),

  commitDetail: (batchId: string, limit = 500) =>
    request<CommitDetailResponse>(`/api/v1/pipeline/commits/${batchId}`, {
      query: { limit },
    }),
};

// ─── Commit history ─────────────────────────────────────────────

export interface CommitHistoryRow {
  batch_id: string;
  started_at: string;
  committed_at: string;
  committed_by: string | null;
  documents_processed: number;
  documents_halted: number;
  triplets_staged: number;
}

export interface CommitTripleRow {
  subject: string;
  predicate: string;
  object: string;
  source_id: string;
  source_url: string;
  run_id: string;
}

export interface CommitDetailResponse {
  batch_id: string;
  committed_at: string;
  triplets_total: number;
  documents: Array<{
    source_id: string;
    source_url: string;
    run_id: string;
    triplets_count: number;
    entity_count: number;
    with_warnings: boolean;
  }>;
  triples: CommitTripleRow[];
  truncated: boolean;
}

export const errorsApi = {
  list: (params: {
    error_type?: ErrorType[];
    status?: string[];
    source_id?: string;
    batch_id?: string;
    pattern_group?: string;
    page?: number;
    page_size?: number;
    sort?: 'created_at' | 'error_type' | 'severity';
  }) =>
    request<ErrorListResponse>('/api/v1/errors', {
      query: {
        error_type: params.error_type,
        status: params.status,
        source_id: params.source_id,
        batch_id: params.batch_id,
        pattern_group: params.pattern_group,
        page: params.page ?? 1,
        page_size: params.page_size ?? 50,
        sort: params.sort ?? 'created_at',
      },
    }),

  get: (errorId: string) =>
    request<ErrorListItem>(`/api/v1/errors/${errorId}`),

  resolve: (
    errorId: string,
    payload: {
      action: ResolutionAction;
      decision_type: string;
      decision_outcome?: string;
      corrected_value?: string;
      note?: string;
      log_as_training_signal?: boolean;
      apply_to_group?: boolean;
    },
  ) =>
    request<{
      error_id: string;
      status: string;
      training_signal_id: string | null;
      affected_count: number;
    }>(`/api/v1/errors/${errorId}/resolve`, { method: 'POST', body: payload }),

  identicalCount: (errorId: string) =>
    request<{
      count: number;
      signature: Record<string, string | null>;
    }>(`/api/v1/errors/${errorId}/identical-count`),

  groupEscalate: (payload: { check_id: string; subject_class?: string; note?: string }) =>
    request<{ candidate_id: string; errors_escalated: number; pattern_group: string }>(
      '/api/v1/errors/group/escalate',
      { method: 'POST', body: payload },
    ),
};

export interface PasteOutcome {
  mode: 'daily_queue' | 'backfill_session';
  input_type: 'text_paste' | 'url_paste';
  source_id: string | null;
  session_id?: string | null;
  success: boolean;
  status: string;
}

export const sourcesApi = {
  list: (includeInactive = false) =>
    request<Source[]>('/api/v1/sources', {
      query: { include_inactive: includeInactive },
    }),

  create: (payload: {
    source_url: string;
    source_type: SourceType;
    retrieval_tier: string;
    topic_tags: string[];
  }) => request<Source>('/api/v1/sources', { method: 'POST', body: payload }),

  patch: (
    sourceId: string,
    payload: {
      source_url?: string;
      source_type?: SourceType;
      retrieval_tier?: string;
      topic_tags?: string[];
    },
  ) => request<Source>(`/api/v1/sources/${sourceId}`, { method: 'PATCH', body: payload }),

  deactivate: (sourceId: string, reason?: string) =>
    request<Source>(`/api/v1/sources/${sourceId}/deactivate`, {
      method: 'POST',
      body: reason ? { reason } : {},
    }),

  reactivate: (sourceId: string) =>
    request<Source>(`/api/v1/sources/${sourceId}/reactivate`, { method: 'POST' }),

  remove: (sourceId: string) =>
    request<void>(`/api/v1/sources/${sourceId}`, { method: 'DELETE' }),

  pollNow: (sourceId: string) =>
    request<RssPollResult>(`/api/v1/sources/${sourceId}/poll-now`, {
      method: 'POST',
    }),

  pollHistory: (sourceId: string, limit = 20) =>
    request<RssPollResult[]>(`/api/v1/sources/${sourceId}/poll-history`, {
      query: { limit },
    }),

  pasteText: (payload: {
    source_url?: string;
    content_text: string;
    source_attribution: string;
    topic_tags?: string[];
    mode?: 'daily_queue' | 'backfill_session';
    publication_date?: string;
    language?: string;
  }) =>
    request<PasteOutcome>('/api/v1/sources/paste/text', {
      method: 'POST',
      body: { input_type: 'text_paste', ...payload },
    }),

  pasteUrl: (payload: {
    source_url: string;
    source_attribution?: string;
    topic_tags?: string[];
    mode?: 'daily_queue' | 'backfill_session';
  }) =>
    request<PasteOutcome>('/api/v1/sources/paste/url', {
      method: 'POST',
      body: { input_type: 'url_paste', ...payload },
    }),

  openBackfillSession: () =>
    request<unknown>('/api/v1/sources/backfill-sessions', { method: 'POST' }),
};

// ─── Ontology (Studio governance) ───────────────────────────────

export interface OntologyGap {
  gap_id: string;
  entity_label: string;
  frequency: number;
  source_count: number;
  candidate_class: string | null;
  candidate_confidence: number | null;
  status: string;
  semantic_cluster_id: string | null;
  first_seen: string;
}

export interface OntologyDraft {
  draft_id: string;
  operation_type: 'add_class' | 'add_property';
  iri: string;
  label: string;
  status: string;
  drafted_by: string;
  reviewed_by: string | null;
  rejection_reason: string | null;
  created_at: string;
}

export interface OntologyClassSummary {
  iri: string;
  local_name: string;
  label: string | null;
  parents: string[];
  children_count: number;
}

export interface OntologyClassDetail extends OntologyClassSummary {
  comment: string | null;
  children: string[];
  used_as_domain: string[];
  used_as_range: string[];
}

export const ontologyApi = {
  listGaps: (params: { status?: string; limit?: number } = {}) =>
    request<OntologyGap[]>('/api/v1/ontology/gaps', {
      query: { status_filter: params.status ?? 'open', limit: params.limit ?? 100 },
    }),

  createDraft: (payload: {
    operation_type: 'add_class' | 'add_property';
    iri: string;
    label: string;
    parent_iri?: string;
    payload?: Record<string, unknown>;
    gap_issue_id?: string;
  }) =>
    request<OntologyDraft>('/api/v1/ontology/drafts', { method: 'POST', body: payload }),

  runSandbox: (draftId: string) =>
    request<OntologyDraft>(`/api/v1/ontology/drafts/${draftId}/sandbox/run`, {
      method: 'POST',
    }),

  requestPromotion: (draftId: string) =>
    request<OntologyDraft>(
      `/api/v1/ontology/drafts/${draftId}/request-promotion`,
      { method: 'POST' },
    ),

  promote: (draftId: string) =>
    request<OntologyDraft>(`/api/v1/ontology/drafts/${draftId}/promote`, {
      method: 'POST',
    }),

  reject: (draftId: string, reason: string) =>
    request<OntologyDraft>(`/api/v1/ontology/drafts/${draftId}/reject`, {
      method: 'POST',
      query: { reason },
    }),

  listClasses: (q?: string, limit = 500) =>
    request<OntologyClassSummary[]>('/api/v1/ontology/classes', {
      query: { q, limit },
    }),

  classDetail: (iri: string) =>
    request<OntologyClassDetail>('/api/v1/ontology/classes/by-iri', {
      query: { iri },
    }),

  runSparql: (query: string) =>
    request<SparqlSelectResult | SparqlAskResult>('/api/v1/ontology/sparql', {
      method: 'POST',
      body: { query },
    }),

  listOverrides: () =>
    request<OntologyOverridesResponse>('/api/v1/ontology/overrides'),

  disableOverride: (payload: {
    iri: string;
    kind: 'property' | 'class';
    reason?: string;
  }) =>
    request<OntologyOverride>('/api/v1/ontology/overrides', {
      method: 'POST',
      body: payload,
    }),

  enableOverride: (iri: string) =>
    request<void>(`/api/v1/ontology/overrides/${encodeURIComponent(iri)}`, {
      method: 'DELETE',
    }),
};

export interface OntologyOverride {
  iri: string;
  kind: 'property' | 'class';
  reason: string | null;
  disabled_at: string;
}

export interface OntologyOverridesResponse {
  items: OntologyOverride[];
  counts: { property: number; class: number };
}

export interface SparqlBinding {
  values: Record<string, string>;
}

export interface SparqlSelectResult {
  kind: 'select';
  columns: string[];
  rows: SparqlBinding[];
  truncated: boolean;
}

export interface SparqlAskResult {
  kind: 'ask';
  boolean: boolean;
}

// ─── Skills (PO-only governance) ────────────────────────────────

export interface SkillSummary {
  skill_id: string;
  active_version: string | null;
  drafts: number;
  candidates: number;
}

export interface SkillDraft {
  draft_id: string;
  skill_id: string;
  version_label: string;
  status: string;
  llm_model: string | null;
  change_rationale: string;
  drafted_by: string;
  approved_by: string | null;
  created_at: string;
}

export interface SkillDraftDetail extends SkillDraft {
  prompt_markdown: string;
  thresholds: Record<string, unknown>;
}

export const skillsApi = {
  list: () => request<SkillSummary[]>('/api/v1/skills'),

  listVersions: (skillId: string) =>
    request<SkillDraft[]>(`/api/v1/skills/${skillId}/versions`),

  getActive: (skillId: string) =>
    request<SkillDraftDetail | null>(`/api/v1/skills/${skillId}/active`),

  createDraft: (payload: {
    skill_id: string;
    version_label: string;
    change_rationale: string;
    prompt_markdown: string;
    llm_model?: string;
    thresholds?: Record<string, unknown>;
    candidate_error_ids?: string[];
  }) => request<SkillDraft>('/api/v1/skills', { method: 'POST', body: payload }),

  runSandbox: (draftId: string) =>
    request<SkillDraft>(`/api/v1/skills/${draftId}/sandbox/run`, { method: 'POST' }),

  promote: (draftId: string) =>
    request<SkillDraft>(`/api/v1/skills/${draftId}/promote`, { method: 'POST' }),
};

// ─── Inference governance ───────────────────────────────────────

export interface InferenceCandidate {
  candidate_id: string;
  pattern_signature: string;
  pattern_description: string;
  construct_sparql: string;
  example_triples: Array<{
    subject: string;
    predicate: string;
    object: string;
    evidence?: string[];
  }>;
  affected_entities: number;
  avg_confidence: number | null;
  status: 'open' | 'deferred' | 'accepted' | 'promoted' | 'rejected';
  deferred_until: string | null;
  defer_reason: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  promoted_rule_id: string | null;
  surfaced_at: string;
}

export interface InferenceRule {
  rule_id: string;
  name: string;
  description: string;
  scope: string | null;
  construct_sparql: string;
  inferred_graph_iri: string | null;
  enabled: boolean;
  triplet_count: number;
  last_run_at: string | null;
  status: 'active' | 'disabled' | 'deprecated' | 'archived';
  promoted_from_candidate: string | null;
  promoted_by: string;
  deprecation_reason: string | null;
  created_at: string;
}

export interface InferenceSandboxResult {
  inferred_triples: Array<{ subject: string; predicate: string; object: string }>;
  triple_count: number;
  distinct_subjects: number;
  distinct_predicates: number;
}

export const inferenceApi = {
  listCandidates: (status: string = 'open', limit = 100) =>
    request<InferenceCandidate[]>('/api/v1/inference/candidates', {
      query: { status, limit },
    }),

  reviewCandidate: (
    candidateId: string,
    payload: {
      action: 'accept_instances' | 'promote_to_rule' | 'defer' | 'reject';
      rule_name?: string | undefined;
      rule_scope?: string | undefined;
      defer_until?: string | undefined;
      defer_reason?: string | undefined;
    },
  ) =>
    request<InferenceCandidate>(`/api/v1/inference/candidates/${candidateId}/action`, {
      method: 'POST',
      body: payload,
    }),

  listRules: (statusFilter?: string) =>
    request<InferenceRule[]>('/api/v1/inference/rules', {
      query: statusFilter ? { status: statusFilter } : {},
    }),

  createRule: (payload: {
    name: string;
    description: string;
    scope?: string;
    construct_sparql: string;
  }) => request<InferenceRule>('/api/v1/inference/rules', { method: 'POST', body: payload }),

  patchRule: (
    ruleId: string,
    payload: { enabled?: boolean; status?: string; deprecation_reason?: string },
  ) =>
    request<InferenceRule>(`/api/v1/inference/rules/${ruleId}`, {
      method: 'PATCH',
      body: payload,
    }),

  runRule: (ruleId: string) =>
    request<InferenceSandboxResult>(`/api/v1/inference/rules/${ruleId}/run`, { method: 'POST' }),

  previewSandbox: (constructSparql: string) =>
    request<InferenceSandboxResult>('/api/v1/inference/sandbox/preview', {
      method: 'POST',
      body: { construct_sparql: constructSparql },
    }),
};

// ─── GIS (spatial annotations) ──────────────────────────────────

export interface GISEntitySuggestion {
  entity_iri: string;
  label: string;
  entity_class: string;
}

export interface GISGeometry {
  entity_iri: string;
  geom_geojson: GeoJSON.Geometry;
  geom_type: string;
  source: 'manual' | 'geocoded' | 'uploaded';
  source_label: string | null;
  set_at: string;
  updated_at: string;
}

export interface GeocodeHit {
  label: string;
  lon: number;
  lat: number;
  osm_type: string | null;
}

export interface UploadedFeature {
  name: string | null;
  geom_geojson: GeoJSON.Geometry;
  geom_type: string;
  properties: Record<string, unknown>;
}

export interface UploadResult {
  format: string;
  feature_count: number;
  features: UploadedFeature[];
  reprojected_from: string | null;
}

async function multipart<T>(path: string, form: FormData): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const token = tokenProvider();
  const resp = await fetch(url, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!resp.ok) {
    let detail: unknown = await resp.text();
    try {
      detail = JSON.parse(detail as string);
    } catch {
      // body wasn't JSON — keep as text
    }
    const d = detail as { detail?: unknown };
    throw new APIError({ status: resp.status, detail: (d.detail ?? detail) as string });
  }
  return (await resp.json()) as T;
}

export interface GeocodeCandidate {
  iri: string;
  label: string;
  entity_class: string;
}

export interface GeocodeCandidatesResponse {
  total: number;
  sample: GeocodeCandidate[];
}

export interface BatchGeocodeResult {
  scanned: number;
  geocoded: number;
  skipped_no_match: number;
  skipped_low_confidence: number;
  failed: number;
  written_triples: number;
  examples: GeocodeCandidate[];
}

export type GeocodeFailureOutcome = 'no_match' | 'low_confidence' | 'failed_api';

export interface FailedGeocodeCandidate {
  entity_iri: string;
  label: string;
  entity_class: string;
  outcome: GeocodeFailureOutcome;
  importance_score: number | null;
  retry_count: number;
  attempted_at: string;
}

export interface FailedGeocodeListResponse {
  total: number;
  items: FailedGeocodeCandidate[];
}

export interface AttemptActionResponse {
  entity_iri: string;
  manual_status: 'pending' | 'resolved' | 'dismissed' | 'removed';
}

export const gisApi = {
  searchEntities: (q: string, limit = 15) =>
    request<GISEntitySuggestion[]>('/api/v1/gis/entities', {
      query: { q, limit },
    }),

  getGeometry: (entityIri: string) =>
    request<GISGeometry | null>(
      `/api/v1/gis/entities/${encodeURIComponent(entityIri)}/geometry`,
    ),

  putGeometry: (
    entityIri: string,
    payload: {
      geom_geojson: GeoJSON.Geometry;
      source: 'manual' | 'geocoded' | 'uploaded';
      source_label?: string | undefined;
    },
  ) =>
    request<GISGeometry>(
      `/api/v1/gis/entities/${encodeURIComponent(entityIri)}/geometry`,
      { method: 'PUT', body: payload },
    ),

  deleteGeometry: (entityIri: string) =>
    request<void>(`/api/v1/gis/entities/${encodeURIComponent(entityIri)}/geometry`, {
      method: 'DELETE',
    }),

  geocode: (q: string) =>
    request<GeocodeHit[]>('/api/v1/gis/geocode', { method: 'POST', query: { q } }),

  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return multipart<UploadResult>('/api/v1/gis/upload', form);
  },

  geocodeCandidates: (limit = 20) =>
    request<GeocodeCandidatesResponse>('/api/v1/gis/geocode-candidates', {
      query: { limit },
    }),

  geocodeMissing: (limit = 50, minConfidence = 0.4) =>
    request<BatchGeocodeResult>('/api/v1/gis/geocode-missing', {
      method: 'POST',
      query: { limit, min_confidence: minConfidence },
    }),

  geocodeFailed: (opts: { iris?: string[]; limit?: number; minConfidence?: number } = {}) =>
    request<BatchGeocodeResult>('/api/v1/gis/geocode-failed', {
      method: 'POST',
      query: { limit: opts.limit ?? 100, min_confidence: opts.minConfidence ?? 0.3 },
      body: opts.iris && opts.iris.length > 0 ? { iris: opts.iris } : undefined,
    }),

  bulkDismissAttempts: (iris: string[]) =>
    request<{ affected: number }>('/api/v1/gis/attempts/bulk-dismiss', {
      method: 'POST',
      body: { iris },
    }),

  bulkRetryAttempts: (iris: string[]) =>
    request<{ affected: number }>('/api/v1/gis/attempts/bulk-retry', {
      method: 'POST',
      body: { iris },
    }),

  failedCandidates: (limit = 50, includeDismissed = false) =>
    request<FailedGeocodeListResponse>('/api/v1/gis/failed-candidates', {
      query: { limit, include_dismissed: includeDismissed },
    }),

  dismissAttempt: (entityIri: string) =>
    request<AttemptActionResponse>(
      `/api/v1/gis/attempts/${encodeURIComponent(entityIri)}/dismiss`,
      { method: 'POST' },
    ),

  retryAttempt: (entityIri: string) =>
    request<AttemptActionResponse>(
      `/api/v1/gis/attempts/${encodeURIComponent(entityIri)}/retry`,
      { method: 'POST' },
    ),
};

// ─── Knowledge graph ────────────────────────────────────────────

export interface EntityLiteral {
  predicate: string;
  predicate_iri: string;
  value: string;
  datatype: string | null;
  lang: string | null;
}

export interface EntityNeighbour {
  predicate: string;
  predicate_iri: string;
  target_iri: string;
  target_label: string;
  target_class: string;
}

export interface EntityDetail {
  iri: string;
  label: string;
  class_name: string;
  types: string[];
  literals: EntityLiteral[];
  outgoing: EntityNeighbour[];
  incoming: EntityNeighbour[];
  truncated: boolean;
}

export const kgApi = {
  subgraph: (entityIri: string, hops: 1 | 2 = 1, limit = 150) =>
    request<SubgraphResponse>('/api/v1/kg/subgraph', {
      query: { entity_iri: entityIri, hops, limit },
    }),

  mapEntities: (opts: { bbox?: string; limit?: number } = {}) =>
    request<MapEntitiesResponse>('/api/v1/kg/map-entities', {
      query: { bbox: opts.bbox, limit: opts.limit ?? 500 },
    }),

  entityDetail: (iri: string) =>
    request<EntityDetail>('/api/v1/kg/entities/by-iri', {
      query: { entity_iri: iri },
    }),
};

// ─── Manual curation ────────────────────────────────────────────

export interface EntitySearchHit {
  entity_iri: string;
  label: string;
  entity_class: string;
  alt_labels: string[];
}

export interface ManualTripletInput {
  subject_iri: string;
  predicate_iri: string;
  object_iri?: string;
  object_literal?: string;
  object_datatype?: string;
  rationale: string;
}

export const curationApi = {
  searchEntities: (q: string, limit = 10) =>
    request<EntitySearchHit[]>('/api/v1/curation/entities/search', {
      query: { q, limit },
    }),

  submitManual: (payload: ManualTripletInput) =>
    request<{ error_id: string; error_type: 'M'; status: string }>(
      '/api/v1/curation/manual',
      { method: 'POST', body: payload },
    ),
};

export const adminApi = {
  audit: (params: { event_type?: string; page?: number; page_size?: number }) =>
    request<AuditPage>('/api/v1/admin/audit', {
      query: {
        event_type: params.event_type,
        page: params.page ?? 1,
        page_size: params.page_size ?? 100,
      },
    }),

  llmProviders: () => request<LLMProvider[]>('/api/v1/admin/llm/providers'),

  health: () => request<HealthSummary>('/api/v1/admin/health'),
};
